import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Truck, FileText, Mail, Phone, Receipt } from 'lucide-react';
import InvoiceDetail from '../../components/admin/InvoiceDetail';
import * as api from '../../lib/api';
import { formatCents, formatDate } from '../../lib/format';
import { vehicleKindMeta } from '../../lib/fit';

const LEASE_STYLES = {
  active: 'bg-emerald-100 text-emerald-800',
  pending: 'bg-sky-100 text-sky-800',
  past_due: 'bg-rose-100 text-rose-800',
  ending: 'bg-amber-100 text-amber-900',
  ended: 'bg-slate-200 text-slate-600',
  cancelled: 'bg-slate-200 text-slate-600',
};

export default function TenantDetailPage() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);
  const [billing, setBilling] = useState(null);
  const [openInvoice, setOpenInvoice] = useState(null);

  useEffect(() => {
    let live = true;
    setState('loading');
    api.adminTenant(userId)
      .then((payload) => { if (live) { setData(payload); setState('ready'); } })
      .catch((err) => { if (live) { setError(err.message); setState('error'); } });

    // Separate request: billing is the slowest part of this page and the
    // contact details above it are what someone opened it for. A failure here
    // must not blank the profile.
    api.adminTenantInvoices(userId)
      .then((payload) => { if (live) setBilling(payload); })
      .catch(() => { if (live) setBilling({ failed: true }); });

    return () => { live = false; };
  }, [userId]);

  if (state === 'loading') {
    return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" /></div>;
  }
  if (state === 'error') {
    return <p className="mx-auto mt-8 max-w-md rounded-lg bg-rose-50 p-4 text-sm text-rose-800">{error}</p>;
  }

  const { tenant, vehicles, leases } = data;

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      <Link to="/admin/tenants" className="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Tenants
      </Link>

      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">
        {tenant.first_name} {tenant.last_name}
      </h1>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
        <span className="inline-flex items-center gap-1.5">
          <Mail className="h-3.5 w-3.5 text-brand-500" aria-hidden="true" />
          <a href={`mailto:${tenant.email}`} className="hover:underline">{tenant.email}</a>
        </span>
        {tenant.phone && (
          <span className="inline-flex items-center gap-1.5">
            <Phone className="h-3.5 w-3.5 text-brand-500" aria-hidden="true" />
            <a href={`tel:${tenant.phone}`} className="hover:underline">{tenant.phone}</a>
          </span>
        )}
        {tenant.status !== 'active' && (
          <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[12px] font-bold text-rose-800">{tenant.status}</span>
        )}
      </div>

      <section className="mt-7">
        <h2 className="mb-2.5 flex items-center gap-1.5 text-sm font-bold text-slate-900">
          <FileText className="h-4 w-4 text-brand-500" aria-hidden="true" /> Leases
        </h2>
        {leases.length === 0 ? (
          <p className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
            This tenant has never held a lease.
          </p>
        ) : (
          <ul className="space-y-2">
            {leases.map((l) => (
              <li key={l.public_id} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-bold text-slate-900">
                      {l.facility_name} · space {l.unit_number}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      From {l.starts_on}
                      {l.ends_on ? ` to ${l.ends_on}` : ' · month to month'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {Number(l.outstanding_cents) > 0 && (
                      <span className="text-sm font-bold text-rose-700">
                        {formatCents(Number(l.outstanding_cents))} due
                      </span>
                    )}
                    <span className="text-sm font-semibold tabular-nums text-slate-900">
                      {formatCents(l.monthly_rate_cents)}/mo
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-[12px] font-bold ${LEASE_STYLES[l.status] ?? LEASE_STYLES.ended}`}>
                      {l.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <BillingHistory payload={billing} onOpen={setOpenInvoice} />

      <section className="mt-7">
        <h2 className="mb-2.5 flex items-center gap-1.5 text-sm font-bold text-slate-900">
          <Truck className="h-4 w-4 text-brand-500" aria-hidden="true" /> Vehicles
        </h2>
        {vehicles.length === 0 ? (
          <p className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
            No vehicles on file — nothing can be fit-checked until there is one.
          </p>
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2">
            {vehicles.map((v) => (
              <li key={v.public_id} className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-sm font-bold text-slate-900">
                  {v.label || [v.year, v.make, v.model].filter(Boolean).join(' ') || vehicleKindMeta(v.kind).label}
                </p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {vehicleKindMeta(v.kind).label} · {v.length_ft}ft × {v.width_ft}ft
                  {v.height_ft ? ` × ${v.height_ft}ft` : ''}
                </p>
                {v.license_plate && (
                  <p className="mt-1 font-mono text-xs text-slate-600">
                    {v.license_plate}{v.plate_state ? ` (${v.plate_state})` : ''}
                  </p>
                )}
                {v.insurance_expires_on && (
                  <p className={`mt-1 text-xs ${new Date(v.insurance_expires_on) < new Date()
                    ? 'font-bold text-rose-700' : 'text-slate-500'}`}>
                    Insurance {new Date(v.insurance_expires_on) < new Date() ? 'expired' : 'expires'} {v.insurance_expires_on}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {openInvoice && (
        <InvoiceDetail invoiceId={openInvoice} onClose={() => setOpenInvoice(null)} />
      )}
    </div>
  );
}

const INVOICE_STATUS_STYLES = {
  paid: 'bg-emerald-100 text-emerald-800',
  open: 'bg-sky-100 text-sky-800',
  past_due: 'bg-rose-100 text-rose-800',
  draft: 'bg-slate-200 text-slate-600',
  void: 'bg-slate-200 text-slate-500',
  refunded: 'bg-amber-100 text-amber-900',
};

/**
 * Everything this tenant has been billed.
 *
 * Scoped server-side: an admin sees the invoices raised at the yards they run,
 * so a tenant renting at two facilities has two histories as far as each admin
 * is concerned. Merging them would leak the other yard's rates.
 */
function BillingHistory({ payload, onOpen }) {
  if (payload === null) {
    return (
      <div className="mt-7 grid place-items-center rounded-xl border border-slate-200 bg-white py-10"
           role="status" aria-label="Loading billing history">
        <Loader2 className="h-5 w-5 animate-spin text-brand-600" aria-hidden="true" />
      </div>
    );
  }

  if (payload.failed) {
    return (
      <div className="mt-7 rounded-xl border border-amber-200 bg-amber-50 p-4">
        <p className="text-sm font-semibold text-amber-900">Could not load billing history.</p>
        <p className="mt-0.5 text-xs text-amber-800">The details above are unaffected.</p>
      </div>
    );
  }

  const { invoices, summary } = payload;

  return (
    <section className="mt-7">
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 text-sm font-bold text-slate-900">
          <Receipt className="h-4 w-4 text-brand-500" aria-hidden="true" /> Billing
        </h2>
        {invoices.length > 0 && (
          <p className="text-xs text-slate-500">
            {formatCents(summary.billed_cents)} billed · {formatCents(summary.paid_cents)} paid
            {summary.outstanding_cents > 0 && (
              <span className="font-bold text-rose-700"> · {formatCents(summary.outstanding_cents)} owed</span>
            )}
          </p>
        )}
      </div>

      {invoices.length === 0 ? (
        <p className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
          Nothing has been invoiced to this tenant yet.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full min-w-[38rem] text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-[12px] font-bold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5">Invoice</th>
                <th className="px-4 py-2.5">Period</th>
                <th className="px-4 py-2.5 text-right">Total</th>
                <th className="px-4 py-2.5 text-right">Owed</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {invoices.map((i) => (
                <tr key={i.public_id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="font-bold text-slate-900">{i.invoice_number}</p>
                    <p className="text-xs text-slate-500">
                      {i.facility_name} · space {i.unit_number}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {formatDate(i.period_start)}
                    <span className="block text-xs text-slate-400">
                      due {formatDate(i.due_on)}
                      {Number(i.days_overdue) > 0 && Number(i.balance_cents) > 0 && (
                        <span className="font-bold text-rose-700"> · {i.days_overdue}d late</span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-900">
                    {formatCents(i.total_cents)}
                  </td>
                  <td className={`px-4 py-3 text-right font-semibold tabular-nums ${
                    Number(i.balance_cents) > 0 ? 'text-rose-700' : 'text-slate-400'}`}>
                    {formatCents(i.balance_cents)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`mr-2 rounded-full px-2 py-0.5 text-[12px] font-bold ${
                      INVOICE_STATUS_STYLES[i.status] ?? INVOICE_STATUS_STYLES.draft}`}>
                      {i.status.replace('_', ' ')}
                    </span>
                    <button type="button" onClick={() => onOpen(i.public_id)}
                            className="text-xs font-bold text-brand-700 hover:underline">
                      Open →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
